import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useReducedMotion} from "framer-motion";

import {TinderMultiLeadStep} from "../../types/tinder_multi_lead/ITinderMultiLeadState";

interface IParams {
    activePropertyId: number | undefined;
    currentIndex: number;
    isOpen: boolean;
    step: TinderMultiLeadStep;
}

/**
 * Drives the deck advance animation: freezes the outgoing deck height into a gray catcher so the
 * new card's content develops INTO it (no fresh-element flicker), locks/resets the modal-body scroll
 * during the transition, and releases everything only once BOTH the exit zoom-through has finished
 * AND the new card's content has landed.
 */
export function useTinderMultiLeadAnimation({activePropertyId, currentIndex, isOpen, step}: IParams) {
    const shouldReduceMotion = useReducedMotion();
    const contentRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const prevIndexRef = useRef(currentIndex);
    const lastDeckHeightRef = useRef<number | null>(null);
    const isAdvancingRef = useRef(false);
    const exitDoneRef = useRef(true);
    const contentReadyRef = useRef(true);
    const activeIdRef = useRef<number | undefined>(undefined);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [frozenHeight, setFrozenHeight] = useState<number | null>(null);

    // Mirror the active id synchronously (render phase). The entering card's onReadyChange fires in a
    // child effect BEFORE any parent effect, so an effect-set ref would still hold the old id and the
    // readiness signal would be dropped as "stale" — deadlocking the release gate on cache-warm content.
    activeIdRef.current = activePropertyId;

    // The advance is released only once BOTH the exit zoom-through has finished AND the new card's
    // content has landed, so the deck height never settles onto a transient loader.
    const releaseAdvanceIfReady = useCallback(() => {
        if (exitDoneRef.current && contentReadyRef.current) {
            setIsAdvancing(false);
            setFrozenHeight(null);
        }
    }, []);

    const handleCardReady = useCallback(
        (propertyId: number, ready: boolean) => {
            // Ignore the exiting card (still mounted by AnimatePresence with the previous property).
            if (propertyId !== activeIdRef.current) {
                return;
            }

            contentReadyRef.current = ready;

            if (ready) {
                releaseAdvanceIfReady();
            }
        },
        [releaseAdvanceIfReady]
    );

    const handleExitComplete = useCallback(() => {
        exitDoneRef.current = true;
        releaseAdvanceIfReady();
    }, [releaseAdvanceIfReady]);

    // Observe the deck via a callback ref so the observer reliably re-attaches across mount/remount
    // (e.g. modal reopen), continuously recording the idle deck height to freeze on the next advance.
    // By the time the advance effect runs the live DOM has already swapped to the new card, so this
    // last-known idle value is the only reliable source for the OUTGOING height.
    const setDeckNode = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        if (!node || typeof ResizeObserver === "undefined") {
            return;
        }

        // Seed synchronously on attach so the very first advance (or the first after a reopen) can
        // freeze a real height even before the observer's initial async callback fires.
        if (!isAdvancingRef.current) {
            lastDeckHeightRef.current = node.offsetHeight;
        }

        const observer = new ResizeObserver(() => {
            if (!isAdvancingRef.current) {
                lastDeckHeightRef.current = node.offsetHeight;
            }
        });

        observer.observe(node);
        observerRef.current = observer;
    }, []);

    useEffect(() => {
        isAdvancingRef.current = isAdvancing;
    }, [isAdvancing]);

    // Freeze the deck height + raise the gray catcher BEFORE paint (useLayoutEffect), reset the
    // scroll to the top, and lock it. Released by releaseAdvanceIfReady once exit + content are done.
    useLayoutEffect(() => {
        if (currentIndex > prevIndexRef.current) {
            exitDoneRef.current = false;
            contentReadyRef.current = activePropertyId === undefined;
            setFrozenHeight(lastDeckHeightRef.current);
            setIsAdvancing(true);

            if (contentRef.current) {
                contentRef.current.scrollTop = 0;
            }
        }

        prevIndexRef.current = currentIndex;
    }, [currentIndex, activePropertyId]);

    useEffect(() => {
        if (isOpen && step === "browse") {
            return;
        }

        setIsAdvancing(false);
        setFrozenHeight(null);
        exitDoneRef.current = true;
        contentReadyRef.current = true;

        if (!isOpen) {
            prevIndexRef.current = 0;
        }
    }, [isOpen, step]);

    return {
        contentRef,
        frozenHeight,
        handleCardReady,
        handleExitComplete,
        isAdvancing,
        setDeckNode,
        shouldReduceMotion: Boolean(shouldReduceMotion)
    };
}
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {useReducedMotion} from "framer-motion";

import {TinderMultiLeadStep} from "../../types/tinder_multi_lead/ITinderMultiLeadState";

interface IParams {
    activePropertyId: number | undefined;
    currentIndex: number;
    isOpen: boolean;
    step: TinderMultiLeadStep;
}

/**
 * Drives the deck advance animation: freezes the outgoing deck height into a gray catcher so the
 * new card's content develops INTO it (no fresh-element flicker), locks/resets the modal-body scroll
 * during the transition, and releases everything only once BOTH the exit zoom-through has finished
 * AND the new card's content has landed.
 */
export function useTinderMultiLeadAnimation({activePropertyId, currentIndex, isOpen, step}: IParams) {
    const shouldReduceMotion = useReducedMotion();
    const contentRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const prevIndexRef = useRef(currentIndex);
    const lastDeckHeightRef = useRef<number | null>(null);
    const isAdvancingRef = useRef(false);
    const exitDoneRef = useRef(true);
    const contentReadyRef = useRef(true);
    const activeIdRef = useRef<number | undefined>(undefined);
    const [isAdvancing, setIsAdvancing] = useState(false);
    const [frozenHeight, setFrozenHeight] = useState<number | null>(null);

    // Mirror the active id synchronously (render phase). The entering card's onReadyChange fires in a
    // child effect BEFORE any parent effect, so an effect-set ref would still hold the old id and the
    // readiness signal would be dropped as "stale" — deadlocking the release gate on cache-warm content.
    activeIdRef.current = activePropertyId;

    // The advance is released only once BOTH the exit zoom-through has finished AND the new card's
    // content has landed, so the deck height never settles onto a transient loader.
    const releaseAdvanceIfReady = useCallback(() => {
        if (exitDoneRef.current && contentReadyRef.current) {
            setIsAdvancing(false);
            setFrozenHeight(null);
        }
    }, []);

    const handleCardReady = useCallback(
        (propertyId: number, ready: boolean) => {
            // Ignore the exiting card (still mounted by AnimatePresence with the previous property).
            if (propertyId !== activeIdRef.current) {
                return;
            }

            contentReadyRef.current = ready;

            if (ready) {
                releaseAdvanceIfReady();
            }
        },
        [releaseAdvanceIfReady]
    );

    const handleExitComplete = useCallback(() => {
        exitDoneRef.current = true;
        releaseAdvanceIfReady();
    }, [releaseAdvanceIfReady]);

    // Observe the deck via a callback ref so the observer reliably re-attaches across mount/remount
    // (e.g. modal reopen), continuously recording the idle deck height to freeze on the next advance.
    // By the time the advance effect runs the live DOM has already swapped to the new card, so this
    // last-known idle value is the only reliable source for the OUTGOING height.
    const setDeckNode = useCallback((node: HTMLDivElement | null) => {
        observerRef.current?.disconnect();
        observerRef.current = null;

        if (!node || typeof ResizeObserver === "undefined") {
            return;
        }

        // Seed synchronously on attach so the very first advance (or the first after a reopen) can
        // freeze a real height even before the observer's initial async callback fires.
        if (!isAdvancingRef.current) {
            lastDeckHeightRef.current = node.offsetHeight;
        }

        const observer = new ResizeObserver(() => {
            if (!isAdvancingRef.current) {
                lastDeckHeightRef.current = node.offsetHeight;
            }
        });

        observer.observe(node);
        observerRef.current = observer;
    }, []);

    useEffect(() => {
        isAdvancingRef.current = isAdvancing;
    }, [isAdvancing]);

    // Freeze the deck height + raise the gray catcher BEFORE paint (useLayoutEffect), reset the
    // scroll to the top, and lock it. Released by releaseAdvanceIfReady once exit + content are done.
    useLayoutEffect(() => {
        if (currentIndex > prevIndexRef.current) {
            exitDoneRef.current = false;
            contentReadyRef.current = activePropertyId === undefined;
            setFrozenHeight(lastDeckHeightRef.current);
            setIsAdvancing(true);

            if (contentRef.current) {
                contentRef.current.scrollTop = 0;
            }
        }

        prevIndexRef.current = currentIndex;
    }, [currentIndex, activePropertyId]);

    useEffect(() => {
        if (isOpen && step === "browse") {
            return;
        }

        setIsAdvancing(false);
        setFrozenHeight(null);
        exitDoneRef.current = true;
        contentReadyRef.current = true;

        if (!isOpen) {
            prevIndexRef.current = 0;
        }
    }, [isOpen, step]);

    return {
        contentRef,
        frozenHeight,
        handleCardReady,
        handleExitComplete,
        isAdvancing,
        setDeckNode,
        shouldReduceMotion: Boolean(shouldReduceMotion)
    };
}
